import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';

import {
  useDiscoverySearchResults,
  useDiscoverySearchStatus,
  useExportDiscoveryCreatorListWithFeedback,
  type CreatorContent,
} from '@/entities/discovery';
import {
  useAddProfilesToList,
  useCreateProfileList,
  useExcludeCreator,
} from '@/entities/saved-creators-list';
import { Button } from '@/shared/components/shadcn/button';
import { ScrollArea } from '@/shared/components/shadcn/scroll-area';
import { trackEvent } from '@/shared/track';
import { showDiscoverySaveToListDialog } from '../overlays/show-discovery-save-to-list-dialog';
import {
  DiscoveryIncrementalState,
  DiscoveryNoResults,
  DiscoveryResultsError,
  NoCreatorsResults,
  PreFirstBatchGuidance,
  StoppedSearchEmptyState,
} from '../ui/search-results/primitives';
import { CreatorsResultsHeader } from '@/features/discovery/ui/discovery-results-header';
import { CreatorsResultsListActions } from '../ui/search-results/result-phase.list-actions';
import { CreatorsMatchedFeatureFilter } from '../ui/search-results/result-phase.matched-feature-filter';
import { Stage } from '@/shared/components/stage/stage';
import { ToggleStage } from '@/shared/components/stage/toggle-stage';
import { List } from '@/shared/components/list/list';
import { CreatorCard } from '../ui/creator-card/creator-card';
import { CreatorCardSkeleton } from '../ui/creator-card/creator-card.skeleton';
import { useCreatorsSelection } from '../hooks/use-creators-selection';

import { useSeedPostStore } from '../stores/seed-post-store';
import { CreatorsResultsEndState } from '../ui/search-results/result-phase.end-state';
import { getCreatorSearchLimitNudgeViewModel } from '@/entities/workspace/util/creator-search-limit-ux';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { CreatorsResultsLowFullMatchGuidance } from '../ui/search-results/result-phase.low-full-match-guidance';
import { DISCOVERY_CHAT_INPUT_ID } from '../ui/discovery-prompt-input/chat-panel-input';
import { useDiscoveryStore } from '../stores/discovery-store';
import { useStopDiscoverySearch } from '../hooks/use-stop-discovery-search';

export function DiscoveryResultsPanel({
  sessionId,
  searchId,
}: {
  sessionId: string;
  searchId?: string | null;
}) {
  const { isSearching, setIsSearching } = useDiscoveryStore();
  const { isStopped, stopSearch } = useStopDiscoverySearch({ sessionId, searchId });
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;
  const searchResultLimit = workspace.constraints.influencer_search_results_limit;
  const navigate = useNavigate();
  const [hideSaved, setHideSaved] = useState(false);
  const [selectedVideoAnalysisFeatures, setSelectedVideoAnalysisFeatures] = useState<string[]>([]);
  const { seedPosts, toggleSeedPost, clearSeedPosts, maxSelectedPosts } = useSeedPostStore();

  const { isSelected, toggle, toggleAll, isAllSelected, getSelectedIds, clearSelection } =
    useCreatorsSelection();
  const selectedCreatorIds = getSelectedIds();
  const searchStatusQuery = useDiscoverySearchStatus(sessionId, searchId, {
    enabled: !isStopped,
  });
  const searchStatus = searchStatusQuery.data;
  const videoAnalysisFeatures = useMemo(() => {
    const uniqueFeatures = new Map<string, string>();

    for (const feature of searchStatus?.post_feature_conditions ?? []) {
      const trimmedFeature = feature.trim();
      if (trimmedFeature.length === 0) continue;
      uniqueFeatures.set(trimmedFeature.toLowerCase(), trimmedFeature);
    }

    return Array.from(uniqueFeatures.values());
  }, [searchStatus?.post_feature_conditions]);

  const isSearchReady =
    searchStatus?.complete === true || (searchStatus?.progress_percentage ?? 0) >= 100;

  const {
    creators,
    isComplete: isSearchComplete,
    dataUpdatedAt,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isFetchNextPageError,
    stats,
    attemptCount,
    fetchNextPage,
    refetch,
  } = useDiscoverySearchResults(
    sessionId,
    searchId,
    {
      required_post_features: selectedVideoAnalysisFeatures,
      show_saved: !hideSaved,
    },
    {
      enabled: isSearchReady && !isStopped,
    },
  );

  const isComplete = isSearchReady && isSearchComplete;

  const createProfileListMutation = useCreateProfileList();
  const addProfilesToListMutation = useAddProfilesToList();
  const excludeCreatorMutation = useExcludeCreator();
  const exportCreatorListWithFeedback = useExportDiscoveryCreatorListWithFeedback();
  const totalCount = stats.total;
  const visibleCreatorIds = useMemo(
    () => creators.map((creator) => creator.creator_id),
    [creators],
  );
  const hasVideoAnalysisFeatureFilter = selectedVideoAnalysisFeatures.length > 0;
  const progress = useMemo(() => {
    if (searchResultLimit <= 0) return 0;
    return Math.min((attemptCount * 30 * 100) / searchResultLimit, 100);
  }, [attemptCount, searchResultLimit]);
  const selectedCount = isAllSelected ? visibleCreatorIds.length : selectedCreatorIds.length;

  useEffect(() => {
    setIsSearching(true);

    if (isComplete) {
      setIsSearching(false);
    }
  }, [isComplete, setIsSearching]);

  useEffect(() => {
    clearSelection();
  }, [clearSelection, hideSaved, selectedVideoAnalysisFeatures]);

  useEffect(() => {
    setSelectedVideoAnalysisFeatures((currentFeatures) => {
      const nextFeatures = currentFeatures.filter((feature) =>
        videoAnalysisFeatures.includes(feature),
      );
      return nextFeatures.length === currentFeatures.length ? currentFeatures : nextFeatures;
    });
  }, [videoAnalysisFeatures]);

  useEffect(() => {
    return () => {
      clearSeedPosts();
    };
  }, [clearSeedPosts]);

  useEffect(() => {
    if (isStopped) return;
    if (!hasNextPage || isFetchingNextPage) return;
    if (isError) return;

    void fetchNextPage();
  }, [dataUpdatedAt, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isStopped]);

  const handleSaveToList = async (): Promise<{ listId: string; title: string } | null> => {
    if (!searchId) return null;
    if (!isAllSelected && selectedCreatorIds.length === 0) return null;
    if (isAllSelected && visibleCreatorIds.length === 0) return null;

    const result = await showDiscoverySaveToListDialog('Discovery creators');
    if (result.action === 'cancel') return null;

    const list =
      result.action === 'create'
        ? await createProfileListMutation.mutateAsync(result.title)
        : { id: result.listId, title: result.title };
    const targetCount = isAllSelected ? visibleCreatorIds.length : selectedCreatorIds.length;

    await addProfilesToListMutation.mutateAsync(
      isAllSelected
        ? hasVideoAnalysisFeatureFilter
          ? {
              listId: list.id,
              request: {
                search_id: searchId,
                creator_ids: visibleCreatorIds,
              },
            }
          : {
              listId: list.id,
              request: {
                search_id: searchId,
                selected_all: true,
              },
            }
        : {
            listId: list.id,
            request: {
              search_id: searchId,
              creator_ids: selectedCreatorIds,
            },
          },
    );

    clearSelection();
    toast(`Saved ${targetCount} creator${targetCount > 1 ? 's' : ''} to "${list.title}"`, {
      action: {
        label: 'View list',
        onClick: () => {
          navigate({
            to: '/workspace/$workspaceId/saved-profiles/$listId',
            params: {
              workspaceId,
              listId: list.id,
            },
          });
        },
      },
    });

    if (result.action === 'create') {
      trackEvent('Saved_list', 'create_list', {
        source: 'save-to-list-dialog',
      });
    }

    return {
      listId: list.id,
      title: list.title,
    };
  };

  const handleSaveAndExport = async () => {
    if (!searchId) return;
    const list = await handleSaveToList();
    if (!list) return;

    trackEvent('Saved_list', 'export', {
      source: 'discovery_search_results',
      list_id: list.listId,
      search_id: searchId,
      isAllSelected,
    });

    await exportCreatorListWithFeedback(
      list.listId,
      list.title,
      isAllSelected
        ? hasVideoAnalysisFeatureFilter
          ? { creator_ids: visibleCreatorIds, selected_all: false }
          : { search_id: searchId, selected_all: true }
        : { creator_ids: selectedCreatorIds, selected_all: false },
    );
  };

  const handleExclude = async (creatorId: string) => {
    await excludeCreatorMutation.mutateAsync(creatorId);
    clearSelection();
    toast('Creator excluded', {
      description: "They won't appear in future searches",
    });
  };

  const handleToggleSeedPost = (post: CreatorContent, fallbackFeatures: string[]) => {
    const result = toggleSeedPost({
      id: post.id,
      posterUrl: post.poster_url,
      sourceUrl: post.source_url,
      content: post.content,
      aiMatchedFeatures:
        post.ai_matched_features?.filter(Boolean) && post.ai_matched_features.length > 0
          ? post.ai_matched_features
          : fallbackFeatures,
    });

    if (!result.ok && result.reason === 'limit') {
      toast('You can select up to 6 posts.');
    }
  };

  const shouldShowSearchLimitNudge = Boolean(searchId) && progress >= 100;

  const handleFocusComposer = () => {
    const inputTarget = window.document.getElementById(DISCOVERY_CHAT_INPUT_ID);
    if (!inputTarget) return;
    inputTarget.focus();
  };

  const handleSearchLimitNudgeClick = () => {
    trackEvent('Plan', 'upgrade_lead', {
      source: 'creator_search_limit_nudge',
      plan: workspace.plan,
      limit: searchResultLimit,
    });

    if (searchLimitNudgeViewModel.action.kind === 'internal') {
      navigate({
        to: '/workspace/$workspaceId/settings/plan',
        params: {
          workspaceId,
        },
      });
      return;
    }

    if (typeof window === 'undefined') return;
    window.open(searchLimitNudgeViewModel.action.href, '_blank', 'noopener,noreferrer');
  };

  const searchLimitNudgeViewModel = getCreatorSearchLimitNudgeViewModel(
    workspace.plan,
    workspaceId,
    searchResultLimit,
  );

  const shouldShowLowFullMatchGuidance = useMemo(() => {
    if (!searchId) return false;
    if (!isSearchReady) return false;
    if (totalCount === 0) return false;
    if (!isComplete) return false;
    if (stats.fullMatched === 0) return false;
    if (hasVideoAnalysisFeatureFilter) return false;

    const fullMatchRate = stats.fullMatched / totalCount;
    return fullMatchRate <= 0.05;
  }, [
    hasVideoAnalysisFeatureFilter,
    isComplete,
    isSearchReady,
    stats.fullMatched,
    searchId,
    totalCount,
  ]);

  return (
    <section className="bg-muted/20 flex h-full min-w-210 flex-1 flex-col overflow-x-auto">
      <CreatorsResultsHeader
        filteredCount={creators.length}
        selectedCount={selectedCount}
        totalCount={totalCount}
        fullLoaded={isComplete}
        isSearchReady={isSearchReady}
        pageLoadPercent={progress}
        initialProgress={10}
        isStopped={isStopped}
        hideSaved={hideSaved}
        hasMatchedFeatureFilter={hasVideoAnalysisFeatureFilter}
        setHideSaved={setHideSaved}
        onSaveToListClick={() => void handleSaveToList()}
        onSaveAndExportClick={() => void handleSaveAndExport()}
      />
      <CreatorsMatchedFeatureFilter
        features={videoAnalysisFeatures}
        selectedFeatures={selectedVideoAnalysisFeatures}
        onSelectedFeaturesChange={setSelectedVideoAnalysisFeatures}
      />
      <CreatorsResultsListActions
        isAllSelected={isAllSelected}
        toggleAll={toggleAll}
        sortBy="ai-match"
        setSortBy={() => {}}
        isSearchComplete={isComplete}
        searchId={searchId ?? ''}
      />

      <ScrollArea className="min-h-0 flex-1" scrollbarClassName="w-2 border-l-0 p-0">
        <div className="mx-auto flex w-full flex-col gap-4 p-6">
          <Stage>
            <Stage.Scene status={!isStopped && creators.length === 0 && isError}>
              <DiscoveryResultsError onRetry={() => void refetch()} />
            </Stage.Scene>
            <Stage.Scene status={!isStopped && creators.length === 0 && !isSearching}>
              <DiscoveryNoResults />
            </Stage.Scene>
            <Stage.Scene default className="flex flex-col gap-4">
              {isSearching && <PreFirstBatchGuidance onStopClick={stopSearch} />}
              {shouldShowLowFullMatchGuidance && (
                <CreatorsResultsLowFullMatchGuidance onRewritePrompt={handleFocusComposer} />
              )}
              <List
                className="flex flex-col gap-4 overflow-visible pb-10"
                data={creators}
                isLoading={isSearching && creators.length === 0}
                loader={Array.from({ length: 3 }, (_, index) => (
                  <CreatorCardSkeleton key={`creator-skeleton-${index}`} />
                ))}
                placeholder={{
                  node:
                    isStopped && totalCount === 0 ? (
                      <StoppedSearchEmptyState />
                    ) : (
                      <NoCreatorsResults className="flex-1" />
                    ),
                }}
                render={(creator) => (
                  <CreatorCard
                    key={creator.creator_id}
                    creator={creator}
                    searchId={searchId ?? undefined}
                    isSelected={isSelected(creator.creator_id)}
                    onSelect={() => toggle(creator.creator_id)}
                    onExclude={() => {
                      void handleExclude(creator.creator_id);
                    }}
                    selectedSeedPostIds={seedPosts.map((post) => post.id)}
                    onToggleSeedPost={handleToggleSeedPost}
                    seedPostLimitReached={seedPosts.length >= maxSelectedPosts}
                    highlightedMatchedFeatures={selectedVideoAnalysisFeatures}
                  />
                )}
              />

              {!isStopped && isSearching && (
                <div className="flex flex-col gap-4">
                  {Array.from({ length: 3 }, (_, index) => (
                    <CreatorCardSkeleton key={`creator-skeleton-${index}`} />
                  ))}
                </div>
              )}

              {shouldShowSearchLimitNudge && (
                <CreatorsResultsEndState
                  viewModel={searchLimitNudgeViewModel}
                  onCtaClick={handleSearchLimitNudgeClick}
                />
              )}
            </Stage.Scene>
          </Stage>

          <ToggleStage status={!isStopped && isFetchingNextPage}>
            <DiscoveryIncrementalState label="Loading more verified creators..." />
          </ToggleStage>

          <ToggleStage status={!isStopped && isFetchNextPageError}>
            <Button variant="outline" className="self-center" onClick={() => void fetchNextPage()}>
              Retry loading results
            </Button>
          </ToggleStage>
        </div>
      </ScrollArea>
    </section>
  );
}
